type BrandLogoProps = {
  /** Rendered height in pixels; width scales from the viewBox aspect ratio. */
  height?: number
  /** Colour of the "FAST AID" wordmark. */
  textColor?: string
  /** Colour of the medical-cross icon (ignored when `iconSrc` is set). */
  iconColor?: string
  className?: string
  /**
   * Uploaded hospital logo (Settings → General) used as the icon. When omitted,
   * the bundled inline medical-cross icon is rendered instead.
   */
  iconSrc?: string
  /**
   * Extra vertical space (in viewBox units) between the "FAST AID" wordmark and
   * the "HOSPITAL" tagline.
   */
  taglineGap?: number
}

/**
 * Inline SVG wordmark.
 *
 * The logo deliberately uses an inline `<svg>` rather than an `<img src>` so the
 * "FAST AID" text is rendered with the site's Sora title font (browsers block web
 * fonts inside SVGs loaded through `<img>`, which caused a fallback font and
 * mismatched sizing). The small "HOSPITAL" tagline sits underneath with a small
 * gap and is stretched to the exact width of the wordmark using `textLength` +
 * `lengthAdjust="spacing"` (spacing-only, so glyphs are not distorted).
 *
 * The icon on the left is taken from the hospital logo uploaded in the
 * dashboard (`hospital_logo`) when available, falling back to the bundled
 * medical-cross icon.
 */
export function BrandLogo({
  height = 42,
  textColor = '#0c193d',
  iconColor = '#055f59',
  className,
  iconSrc,
  taglineGap = 0,
}: BrandLogoProps) {
  const viewBoxWidth = 155.39
  const viewBoxHeight = 35.11 + taglineGap
  // Shared wordmark geometry: both lines start at the same x and are forced to
  // the same width so the tagline always lines up with "FAST AID".
  const wordmarkX = 40
  const wordmarkWidth = 115.39
  // Slightly tighter than the wordmark so the tagline's letter spacing is a bit
  // less stretched.
  const taglineWidth = 110
  const wordmarkY = 25
  const taglineX = wordmarkX + 1
  const taglineY = 34.2 + taglineGap
  // The site's title font (see `--title-fonts` in main.css). Applied inline on
  // the SVG text as well as via the `.brand-logo__text`/`.brand-logo__tagline`
  // classes so the wordmark never falls back to a browser default font.
  const titleFontFamily = "var(--title-fonts, var(--font-title, 'Sora', sans-serif))"

  return (
    <svg
      className={className}
      width={(viewBoxWidth / viewBoxHeight) * height}
      height={height}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="Fast Aid Hospital"
    >
      <text
        x={wordmarkX}
        y={wordmarkY}
        className="brand-logo__text"
        fill={textColor}
        style={{ fontFamily: titleFontFamily }}
        textLength={wordmarkWidth}
        lengthAdjust="spacing"
      >
        FAST AID
      </text>
      <text
        x={taglineX}
        y={taglineY}
        className="brand-logo__tagline"
        fill={textColor}
        style={{ fontFamily: titleFontFamily }}
        textLength={taglineWidth}
        lengthAdjust="spacing"
      >
        HOSPITAL
      </text>
      {iconSrc ? (
        <image
          href={iconSrc}
          x="0"
          y="0"
          width="34"
          height="35"
          preserveAspectRatio="xMinYMid meet"
        />
      ) : (
        <>
          <path
            d="M31.4,2.7v16.55c0,8.57-6.97,15.54-15.54,15.54S.32,27.82.32,19.25V2.7h10.47v-.84c0-.84.68-1.52,1.52-1.52.42,0,.8.17,1.07.45s.45.66.45,1.07v6.08c0,.84-.68,1.52-1.52,1.52s-1.52-.68-1.52-1.52v-.44h-5.67v11.75c0,5.92,4.82,10.74,10.74,10.74s10.74-4.82,10.74-10.74V7.5h-5.63v.44c0,.84-.68,1.52-1.52,1.52s-1.52-.68-1.52-1.52V1.85c0-.84.69-1.52,1.52-1.52.42,0,.8.17,1.07.45s.45.66.45,1.07v.84h10.43Z"
            fill={iconColor}
          />
          <path
            d="M15.9,19.83c1.65,0,2.99-1.34,2.99-2.99v-1.03h1.45c1.36,0,2.48,1.11,2.48,2.48s-1.12,2.48-2.48,2.48h-1.96v1.96c0,1.36-1.11,2.48-2.48,2.48s-2.48-1.11-2.48-2.48v-1.96h-1.96c-1.36,0-2.48-1.11-2.48-2.48,0-.68.28-1.3.73-1.75s1.07-.73,1.75-.73h1.45v1.03c0,1.65,1.34,2.99,2.99,2.99Z"
            fill={iconColor}
          />
          <rect x="13.89" y="11.33" width="4.01" height="7.29" rx="2" ry="2" fill={iconColor} />
        </>
      )}
    </svg>
  )
}
